import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styled from '@emotion/styled';

import useOutsideClick from '../../hooks/ui-control/useOutsideClick';

interface ClickOutDropDownProps {
  children: React.ReactNode;
  label: any;
  marginTop?: string;
  borderRadius?: string;
  posRight?: string;
  tableFilter?: boolean;
  maxHeight?: string;
}

const DropDownTemplate: React.FC<ClickOutDropDownProps> = ({
  label,
  children,
  marginTop,
  borderRadius,
  posRight,
  tableFilter,
  maxHeight,
}) => {
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    right?: number;
    openUpward: boolean;
  }>({ top: 0, left: 0, openUpward: false });

  const dropDownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useOutsideClick(
    dropDownRef,
    () => {
      setShowDropDown(false);
    },
    '#root',
  );

  // Calculate position when dropdown opens
  useLayoutEffect(() => {
    if (showDropDown && triggerRef.current && dropDownRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = dropDownRef.current.offsetHeight || 300; // fallback height
      const viewportHeight = window.innerHeight;
      const marginTopValue = parseInt(marginTop || '8', 10);

      // Check if there's enough space below
      const spaceBelow = viewportHeight - triggerRect.bottom - marginTopValue;
      const spaceAbove = triggerRect.top - marginTopValue;

      // Determine if we should open upward
      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      // Calculate position
      const position: any = {
        left: triggerRect.left,
        openUpward,
      };

      if (openUpward) {
        // Position above the trigger
        position.top = triggerRect.top - marginTopValue;
      } else {
        // Position below the trigger (default)
        position.top = triggerRect.bottom + marginTopValue;
      }

      // Handle right positioning if specified
      if (posRight) {
        position.right = window.innerWidth - triggerRect.right;
        delete position.left;
      }

      setDropdownPosition(position);
    }
  }, [showDropDown, marginTop, posRight]);

  // Update position on scroll and resize
  useEffect(() => {
    if (!showDropDown) return;

    const updatePosition = () => {
      if (triggerRef.current && dropDownRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const dropdownHeight = dropDownRef.current.offsetHeight || 300;
        const viewportHeight = window.innerHeight;
        const marginTopValue = parseInt(marginTop || '8', 10);

        const spaceBelow = viewportHeight - triggerRect.bottom - marginTopValue;
        const spaceAbove = triggerRect.top - marginTopValue;
        const openUpward =
          spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

        const position: any = {
          left: triggerRect.left,
          openUpward,
        };

        if (openUpward) {
          position.top = triggerRect.top - marginTopValue;
        } else {
          position.top = triggerRect.bottom + marginTopValue;
        }

        if (posRight) {
          position.right = window.innerWidth - triggerRect.right;
          delete position.left;
        }

        setDropdownPosition(position);
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showDropDown, marginTop, posRight]);

  return (
    <DropDownContainer>
      <DropDownLabel
        ref={triggerRef}
        onClick={() => setShowDropDown(!showDropDown)}
      >
        {label}
      </DropDownLabel>
      {showDropDown &&
        createPortal(
          <DropDownContentWrapper
            ref={dropDownRef}
            maxHeight={maxHeight}
            marginTop={marginTop}
            borderRadius={borderRadius}
            positionData={dropdownPosition}
            onClick={() => setShowDropDown(!showDropDown)}
            className={clsx(
              showDropDown ? 'dropDownContent' : '',
              tableFilter && 'tableFilter',
              dropdownPosition.openUpward && 'openUpward',
            )}
          >
            {React.Children.map(children, (child: any) =>
              React.cloneElement(child, {
                ...child.prop,
                setShowDropDown,
              }),
            )}
          </DropDownContentWrapper>,
          document.body,
        )}
    </DropDownContainer>
  );
};

export const ClickOutDropDown: React.FC<ClickOutDropDownProps> = (props) => {
  return <DropDownTemplate {...props} />;
};

const DropDownContainer = styled.div`
  position: relative;

  div.dropDownContent {
    opacity: 1;
    pointer-events: auto;
  }
`;

const DropDownLabel = styled.div`
  display: flex;
  align-items: center;
`;

interface DropDownContentWrapperProps {
  marginTop?: string;
  borderRadius?: string;
  posRight?: string;
  maxHeight?: string;
  positionData?: {
    top: number;
    left?: number;
    right?: number;
    openUpward: boolean;
  };
}

const DropDownContentWrapper = styled.div<DropDownContentWrapperProps>`
  position: fixed;
  width: fit-content;
  border-radius: ${({ borderRadius }) => borderRadius || '10px'};
  background-color: white;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.05);
  max-height: ${({ maxHeight }) => maxHeight || '400px'};
  /* border: 1px solid ${({ theme }) => theme.palette.borderColor}; */
  /* box-shadow: 1px 4px 12px -1px rgba(44, 78, 39, 0.15); */
  box-shadow: 1px 4px 12px -1px #2c4e2726;

  overflow: auto;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease-in-out;
  z-index: 9999;
  min-width: fit-content;
  display: flex;

  &::-webkit-scrollbar {
    display: none;
  }

  /* right: -400px; */

  /* Dynamic positioning based on calculated position */
  ${({ positionData }) =>
    positionData &&
    `
    top: ${positionData.top}px;
    ${positionData.left !== undefined ? `left: ${positionData.left}px;` : ''}
    ${positionData.right !== undefined ? `right: ${positionData.right}px;` : ''}
  `}

  /* When opening upward, transform from bottom */
  &.openUpward {
    transform: translateY(-100%);
  }

  &.dropDownContent {
    opacity: 1;
    pointer-events: auto;
  }

  /* For small screens, turn it into a modal with full-screen overlay */
  &.tableFilter {
    @media (max-width: 884px) {
      position: fixed;
      width: 80vw;
      left: 0;
      top: 0;
      border: 0;
      border-radius: 0;
      display: flex;
      justify-content: center;
      width: 100vw;
      height: 100vh;
      background: rgba(142, 125, 125, 0.448);
      z-index: 3000;
      margin: 0;
      transform: none;
      & > * {
        border-radius: 16px !important;
        background-color: white !important;
        box-shadow: 0 4px 28px 0 rgba(0, 0, 0, 0.08);
      }
    }
  }
`;
